import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PaginationItem from '@mui/material/PaginationItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import { clampPage, pageCountOf, paginationTokens } from '../clientPagination';
import type { IntlLike } from '../clientPresentation';

export type ClientPaginationProps = {
  page: number;
  total: number;
  pageSize: number;
  onPage: (page: number) => void;
  intl: IntlLike;
};

// The first ClientV2 piece built on the Soft Depth MUI theme (component.tsx
// wraps it in <SoftDepthScope>): every colour, radius and size comes from the
// theme's MuiPaginationItem / MuiOutlinedInput overrides. The one class left,
// `clientv2-pagination`, is only the page-load entrance cascade and its
// reduced-motion kill in styles.scss — choreography, not appearance.
// @soft-depth — npm run scan:soft-depth rejects raw colours and font sizes here.
//
// Below 1024px the info and jump controls share the first line and the page
// buttons take their own full-width, side-scrolling row underneath (±2 window
// plus first/last can run to 11 buttons).
const NARROW = '@media (max-width: 1024px)';

const ClientPagination = ({ page, total, pageSize, onPage, intl }: ClientPaginationProps) => {
  const [jumpValue, setJumpValue] = useState('');

  if (total === 0) return null;

  const pageCount = pageCountOf(total, pageSize);
  const safePage = clampPage(page, total, pageSize);
  const start = safePage * pageSize + 1;
  const end = Math.min(start + pageSize - 1, total);
  const tokens = paginationTokens(safePage, pageCount);

  const jump = () => {
    const requested = Number.parseInt(jumpValue, 10);
    if (Number.isNaN(requested)) return;
    onPage(clampPage(requested - 1, total, pageSize));
    setJumpValue('');
  };

  return (
    <Stack
      component="nav"
      className="clientv2-pagination"
      aria-label={intl.formatMessage({ id: 'clientv2-pagination-label' })}
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.75,
        pt: 1.5,
        px: 0.5,
        pb: 0.25,
        [NARROW]: { flexWrap: 'wrap' }
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        data-testid="pagination-info"
        sx={{ mr: 'auto', fontVariantNumeric: 'tabular-nums', [NARROW]: { order: 1 } }}
      >
        {intl.formatMessage({ id: 'clientv2-pagination-info' }, { start, end, total })}
      </Typography>

      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 0.75,
          [NARROW]: {
            order: 3,
            flexBasis: '100%',
            justifyContent: 'center',
            mt: 0.5,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' }
          }
        }}
      >
        <PaginationItem
          type="previous"
          slots={{ previous: ChevronLeft }}
          slotProps={{ previous: { size: 16 } }}
          aria-label={intl.formatMessage({ id: 'clientv2-pagination-previous' })}
          disabled={safePage === 0}
          onClick={() => onPage(safePage - 1)}
        />

        {tokens.map(token =>
          typeof token === 'number' ? (
            <PaginationItem
              key={token}
              type="page"
              page={token + 1}
              selected={token === safePage}
              aria-current={token === safePage ? 'page' : undefined}
              aria-label={intl.formatMessage({ id: 'clientv2-pagination-page' }, { page: token + 1 })}
              onClick={() => onPage(token)}
            />
          ) : (
            <PaginationItem key={token} type="start-ellipsis" aria-hidden="true" />
          )
        )}

        <PaginationItem
          type="next"
          slots={{ next: ChevronRight }}
          slotProps={{ next: { size: 16 } }}
          aria-label={intl.formatMessage({ id: 'clientv2-pagination-next' })}
          disabled={safePage === pageCount - 1}
          onClick={() => onPage(safePage + 1)}
        />
      </Stack>

      {pageCount > 8 && (
        <Stack
          component="label"
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 0.625,
            ml: 1.25,
            pl: 1.25,
            borderLeft: 1,
            borderColor: 'grey.200',
            color: 'text.secondary',
            typography: 'caption',
            [NARROW]: { order: 2, ml: 0, pl: 0, borderLeft: 0 }
          }}
        >
          <span>{intl.formatMessage({ id: 'clientv2-pagination-jump' })}</span>
          <OutlinedInput
            type="number"
            value={jumpValue}
            placeholder={String(safePage + 1)}
            inputProps={{
              min: 1,
              max: pageCount,
              'aria-label': intl.formatMessage({ id: 'clientv2-pagination-jump-label' })
            }}
            onChange={event => setJumpValue(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') jump();
            }}
            sx={{ width: 46, height: 30, '& input': { typography: 'subtitle2', textAlign: 'center', px: 0.5 } }}
          />
          <span>{intl.formatMessage({ id: 'clientv2-pagination-page-unit' })}</span>
        </Stack>
      )}
    </Stack>
  );
};

export default injectIntl(ClientPagination);
